import { GenericFolder } from "encharge-domain/lib/definitions/ambient/folder";
import _ from "lodash";
import { CustomObjectSchema } from "encharge-domain/definitions/CustomObjectSchema";
import { endUsersObjectType } from "encharge-domain/lib/helpers/sync_engine_helper";
import findItemFolder from "components/Items/findItemFolder";

export const combineSchemaAndFieldIds = (
  schemaName: string,
  fieldName: string
) => `${schemaName}___${fieldName}`;

export const splitSchemaAndFieldIds = (
  combinedId: string
): { schemaName: string; fieldName: string } => {
  const parts = combinedId.split("___");
  // Ids stored before custom objects existed are a bare field name, and back
  // then only person fields could be stored. Without this they would split
  // into schemaName = the whole name and an empty fieldName, so the field never
  // resolves and its folder looks empty.
  if (parts.length === 1) {
    return {
      schemaName: combinedId ? endUsersObjectType : "",
      fieldName: combinedId,
    };
  }
  const schemaName = parts[0];
  const fieldName = parts.slice(1).join("___");
  return { schemaName, fieldName };
};

/**
 * Find the folder a field was moved to, or undefined if it sits in no folder.
 *
 * Folders hold fields by their combined object + field id. Folders created
 * before custom objects existed hold the bare field name instead, and back then
 * only person fields could be put in a folder.
 *
 * Root and the auto folders are skipped by `findItemFolder`. That matters most
 * here: root holds a bare copy of nearly every person field, because
 * `fixOrphaned` keys sidebar items by the bare name and so treats every
 * foldered field as an orphan — matching root there would hide the real folder.
 */
export const findFieldFolder = ({
  folders,
  objectType,
  fieldName,
}: {
  folders: GenericFolder[] | undefined;
  objectType: string;
  fieldName: string;
}): GenericFolder | undefined => {
  const fieldId = combineSchemaAndFieldIds(objectType, fieldName);
  return findItemFolder({
    folders,
    matches: (childId) =>
      childId === fieldId ||
      (objectType === endUsersObjectType && childId === fieldName),
  });
};

/**
 * Compute static folders for custom fields
 */
export const getPersonFieldsStaticFolders = (schemas: CustomObjectSchema[]) => {
  const foldersPerObject: GenericFolder[] = _.reduce(
    schemas,
    (acc, schema) => {
      const schemaFolder: GenericFolder = {
        type: "folder",
        folderType: "personFields",
        id: `schema-${schema.name}`,
        name: `${schema.displayNameSingular} Fields`,
        childrenIds: _.map(schema.fields, (field) =>
          combineSchemaAndFieldIds(schema.name, field.name)
        ),
      };
      acc.push(schemaFolder);
      return acc;
    },
    [] as GenericFolder[]
  );

  // combine all objects fields into one folder
  const allFolder: GenericFolder = {
    type: "folder",
    folderType: "personFields",
    id: "allPersonFields",
    name: "All Fields",
    childrenIds: _.flatten(_.map(foldersPerObject, "childrenIds")) as any,
  };

  // Create folders by fields that have dot in them
  const personFields = _.find(schemas, ["name", endUsersObjectType])?.fields;
  const servicesFolders = _.reduce(
    personFields,
    (acc, field) => {
      if (field.name.includes(".")) {
        // folder name is everything before the first dot
        const folderName = field.name.split(".")[0];
        if (folderName) {
          // create this folder if seen for the first time
          if (!acc[folderName]) {
            acc[folderName] = {
              type: "folder",
              folderType: "personFields",
              id: folderName,
              name: formatAutoFolderName(folderName),
              childrenIds: [],
            };
          }
          // add current field to folder
          acc[folderName]!.childrenIds!.push(
            combineSchemaAndFieldIds(endUsersObjectType, field.name)
          );
        }
      }
      return acc;
    },
    {} as Dictionary<GenericFolder>
  );

  return [allFolder, ...foldersPerObject, ..._.values(servicesFolders)];
};

const formatAutoFolderName = (name: string) => {
  return `${name[0].toUpperCase()}${name.substr(1)}`;
};
